import { isServer } from '@/shared/consts/server';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
type Options = Omit<RequestInit, 'method' | 'body'>;
type ActionParams = Options & { method: Method; body?: object };
type InterceptorHandler<T extends Request | Response> = (param: T) => T;
type BaseHeaders = { [key: string]: string };

export class Fetcher {
  private requestInterceptors: InterceptorHandler<Request>[] = [];
  private responseInterceptors: InterceptorHandler<Response>[] = [];
  private baseUrl: string = '';
  private baseHeaders: BaseHeaders = {};
  private baseOptions: Options = {};

  constructor(options?: { baseUrl?: string; baseHeaders?: BaseHeaders; baseOptions?: Options }) {
    const { baseUrl, baseHeaders, baseOptions } = options ?? {};

    if (baseUrl) {
      this.baseUrl = baseUrl;
    }

    if (baseHeaders) {
      this.baseHeaders = baseHeaders;
    }

    if (baseOptions) {
      this.baseOptions = baseOptions;
    }
  }

  addRequestInterceptor(handler: InterceptorHandler<Request>) {
    this.requestInterceptors.push(handler);
  }

  addResponseInterceptor(handler: InterceptorHandler<Response>) {
    this.responseInterceptors.push(handler);
  }

  get<T>(url: string, options?: Options) {
    return this.action<T>(url, {
      method: 'GET',
      ...options,
    });
  }

  post<T>(url: string, data: object, options?: Options) {
    return this.action<T>(url, {
      method: 'POST',
      body: data,
      ...options,
    });
  }

  patch<T>(url: string, data: object, options?: Options) {
    return this.action<T>(url, {
      method: 'PATCH',
      body: data,
      ...options,
    });
  }

  put<T>(url: string, data: object, options?: Options) {
    return this.action<T>(url, {
      method: 'PUT',
      body: data,
      ...options,
    });
  }

  delete<T>(url: string, data: object, options?: Options) {
    return this.action<T>(url, {
      method: 'DELETE',
      body: data,
      ...options,
    });
  }

  private async action<T>(url: string, params: ActionParams): Promise<T> {
    const { body, method, ...rest } = params;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const headers = {
      ...this.baseHeaders,
      ...rest.headers,
    } as Record<string, string>;

    const options = {
      ...this.baseOptions,
      ...rest,
    };

    if (isFormData) {
      delete headers['Content-Type'];
    }

    const normalizedUrl = this.normalizeUrl(`${this.baseUrl}${url}`);
    const request = this.requestHandler(
      new Request(normalizedUrl, {
        ...options,
        method,
        headers,
        body:
          headers['Content-Type'] === 'application/json'
            ? JSON.stringify(body)
            : (body as BodyInit),
      }),
    );

    const response = this.responseHandler(await fetch(request));

    if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw (await response.json()) as {
        detailErrorMessage: string;
        errorCode: string;
        errorMessage: string;
      };
    }

    const disposition = response.headers.get('Content-Disposition');
    const contentType = response.headers.get('Content-Type');

    if (disposition?.includes('attachment')) return (await response.blob()) as T;
    if (contentType?.includes('text/')) return (await response.text()) as T;
    if (
      contentType?.includes('image/') ||
      contentType?.includes('audio/') ||
      contentType?.includes('video/') ||
      contentType?.includes('application/vnd') ||
      contentType === 'application/octet-stream' ||
      contentType === 'application/zip'
    )
      return (await response.blob()) as T;

    return (await response.json()) as T;
  }

  private requestHandler(request: Request) {
    return this.requestInterceptors.reduce((prev, listener) => {
      return listener(prev);
    }, new Request(request));
  }

  private responseHandler(response: Response) {
    return this.responseInterceptors.reduce(
      (prev, listener) => {
        return listener(prev);
      },
      new Response(response.body, response),
    );
  }

  private normalizeUrl(url: string) {
    const result = url.trim();

    if (result.startsWith('http') || result.startsWith('https')) {
      return result;
    }

    if (result[0] !== '/') {
      return '/' + result;
    }

    return result;
  }
}

function getBaseUrl() {
  if (isServer && process.env.RUNTIME_ENV === 'docker') {
    return process.env.INTERNAL_URL;
  }

  return process.env.NEXT_PUBLIC_API_ENDPOINT;
}

export const fetcher = new Fetcher({
  baseUrl: getBaseUrl(),
  baseHeaders: {
    'Content-Type': 'application/json',
    'View-Path': isServer ? '' : window.location.href, // 서버측 API 히스토리 조회를 위해 추가
  },
  baseOptions: {
    credentials: 'include',
  },
});
